// ** React Imports
import { useState, useRef, useEffect, SyntheticEvent, Fragment, ReactNode } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { styled, Theme, type SxProps, keyframes, alpha } from '@mui/material/styles'
import Fade from '@mui/material/Fade'
import Popper from '@mui/material/Popper'
import useMediaQuery from '@mui/material/useMediaQuery'
import MuiMenu, { MenuProps } from '@mui/material/Menu'
import MuiMenuItem, { MenuItemProps } from '@mui/material/MenuItem'
import Typography, { TypographyProps } from '@mui/material/Typography'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Third Party Components
import PerfectScrollbarComponent from 'react-perfect-scrollbar'

// ** Type Imports
import { Settings } from 'src/@core/context/settingsContext'

// ** Custom Components Imports
import CustomChip from 'src/@core/components/mui/chip'
import { AppDispatch } from '@/src/store'
import { useDispatch } from 'react-redux'
import {
  deleteAllNotificationsOnServer,
  markAllNotificationsReadOnServer,
  markNotificationReadOnServer
} from '@/src/store/apps/resto365Notifications'
import { Button, Drawer, InputAdornment, Paper, PaperProps, Stack, TextField } from '@mui/material'
import GygButton from '@/src/gygComponents/gygButton'
import {
  getNotificationSoundEnabled,
  setNotificationSoundEnabled,
  NOTIFICATION_SOUND_CHANGE_EVENT
} from '@/src/@core/lib/notificationSoundPreference'

export type NotificationsType = {
  meta: string
  title: string
  subtitle: string
  read?: boolean

  /** From Redux {@link Notification.clientKey}; required to mark a single item read. */
  clientKey: string
}

/** Optional trigger styling for `sidebar` mode (e.g. BHYBE light rail — icon-only, dark icon). */
export type NotificationSidebarTriggerProps = {
  iconOnly?: boolean
  bellIcon?: string
  bellIconColor?: string
  iconButtonSx?: SxProps<Theme>
}

interface Props {
  settings: Settings
  notifications: NotificationsType[]
  isSocketConnected: boolean
  sidebar?: boolean
  sidebarTrigger?: NotificationSidebarTriggerProps
}

// ** Styled Menu component
const Menu = styled(MuiMenu)<MenuProps>(({ theme }) => ({
  '& .MuiMenu-paper': {
    width: 500,
    overflow: 'hidden',
    marginTop: theme.spacing(4.25),
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  '& .MuiMenu-list': {
    padding: 0,
    '& .MuiMenuItem-root': {
      margin: 0,
      borderRadius: 0,
      padding: theme.spacing(4, 6),
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      }
    }
  }
}))

// ** Styled MenuItem component
const MenuItem = styled(MuiMenuItem)<MenuItemProps>(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}))

// ** Styled PerfectScrollbar component
const PerfectScrollbar = styled(PerfectScrollbarComponent)({
  maxHeight: 349
})

// ** Styled component for the title in MenuItems
const MenuItemTitle = styled(Typography)<TypographyProps>({
  fontWeight: 500,
  flex: '1 1 100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
})

// ** Styled component for the subtitle in MenuItems
const MenuItemSubtitle = styled(Typography)<TypographyProps>({
  flex: '1 1 100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
})

const Section = styled(Paper)<PaperProps>(() => ({
  square: false,
  borderRadius: '20px',
  padding: '20px'
}))

const ScrollWrapper = ({ children, hidden }: { children: ReactNode; hidden: boolean }) => {
  if (hidden) {
    return <Box sx={{ maxHeight: 349, overflowY: 'auto', overflowX: 'hidden' }}>{children}</Box>
  } else {
    return <PerfectScrollbar options={{ wheelPropagation: false, suppressScrollX: true }}>{children}</PerfectScrollbar>
  }
}

const OFFLINE_LABEL = 'X'

const bellWiggle = keyframes`
  0% { transform: rotate(0deg); }
  12% { transform: rotate(14deg); }
  24% { transform: rotate(-14deg); }
  36% { transform: rotate(8deg); }
  48% { transform: rotate(-8deg); }
  60% { transform: rotate(0deg); }
  100% { transform: rotate(0deg); }
`

const NotificationDropdown = (props: Props) => {
  // ** Props
  const { settings, notifications, isSocketConnected, sidebar = false, sidebarTrigger } = props
  const dispatch = useDispatch<AppDispatch>()

  // ** States
  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null)

  /** Anchor Popper to the bell glyph (not the whole IconButton) so the bubble arrow points at the bell. */
  const bellAnchorRef = useRef<HTMLElement | null>(null)
  const [highlightBubble, setHighlightBubble] = useState<{ text: string } | null>(null)
  const [popperAnchor, setPopperAnchor] = useState<HTMLElement | null>(null)
  const [bellPulse, setBellPulse] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // ** Hook
  const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))

  // ** Vars
  const { direction, navCollapsed } = settings

  const handleDropdownOpen = (event: SyntheticEvent) => {
    setAnchorEl(event.currentTarget)
  }

  const closePanel = () => {
    setAnchorEl(null)
    setSearchQuery('')
  }

  const handleMarkAllRead = () => {
    void dispatch(markAllNotificationsReadOnServer())
  }

  const handleClearAll = () => {
    void dispatch(deleteAllNotificationsOnServer())
    setSearchQuery('')
  }

  const filteredNotifications = searchQuery.trim()
    ? notifications.filter(n => {
        const q = searchQuery.trim().toLowerCase()

        return (
          n.title.toLowerCase().includes(q) || n.subtitle.toLowerCase().includes(q) || n.meta.toLowerCase().includes(q)
        )
      })
    : notifications

  const handleMarkOneRead = (clientKey: string) => {
    if (!clientKey) return
    void dispatch(markNotificationReadOnServer(clientKey))
  }

  const unreadCount = () => {
    return notifications.filter(n => n.read !== true).length
  }

  useEffect(() => {
    setSoundEnabled(getNotificationSoundEnabled())
    const sync = () => setSoundEnabled(getNotificationSoundEnabled())
    window.addEventListener('storage', sync)
    window.addEventListener(NOTIFICATION_SOUND_CHANGE_EVENT, sync as EventListener)

    return () => {
      window.removeEventListener('storage', sync)
      window.removeEventListener(NOTIFICATION_SOUND_CHANGE_EVENT, sync as EventListener)
    }
  }, [])

  const toggleNotificationSound = () => {
    const next = !soundEnabled
    setSoundEnabled(next)
    setNotificationSoundEnabled(next)
  }

  useEffect(() => {
    let clearBubbleTimer: number | undefined
    let clearPulseTimer: number | undefined
    const onHighlight = (e: Event) => {
      const ce = e as CustomEvent<{ message?: string; durationMs?: number }>
      const text = ce.detail?.message?.trim()
      if (!text) return
      if (clearBubbleTimer) window.clearTimeout(clearBubbleTimer)
      if (clearPulseTimer) window.clearTimeout(clearPulseTimer)
      setHighlightBubble({ text })
      requestAnimationFrame(() => {
        const mine = bellAnchorRef.current
        if (!mine) {
          setPopperAnchor(null)

          return
        }
        const rMine = mine.getBoundingClientRect()
        if (rMine.width < 1 || rMine.height < 1) {
          setPopperAnchor(null)

          return
        }
        const firstVisible = Array.from(
          document.querySelectorAll<HTMLElement>('[data-notification-bell-anchor="true"]')
        ).find(el => {
          const r = el.getBoundingClientRect()

          return r.width >= 1 && r.height >= 1
        })
        if (!firstVisible || firstVisible !== mine) {
          setPopperAnchor(null)

          return
        }
        setPopperAnchor(mine)
      })
      setBellPulse(true)
      const ms = typeof ce.detail?.durationMs === 'number' ? ce.detail.durationMs : 12_000
      clearBubbleTimer = window.setTimeout(() => {
        setHighlightBubble(null)
        setPopperAnchor(null)
      }, ms)
      clearPulseTimer = window.setTimeout(() => setBellPulse(false), 2600)
    }
    window.addEventListener('bhyve-bell-highlight', onHighlight as EventListener)

    return () => {
      window.removeEventListener('bhyve-bell-highlight', onHighlight as EventListener)
      if (clearBubbleTimer) window.clearTimeout(clearBubbleTimer)
      if (clearPulseTimer) window.clearTimeout(clearPulseTimer)
    }
  }, [])

  const bellMotionSx = bellPulse
    ? {
        animation: `${bellWiggle} 1.2s ease-in-out 2`
      }
    : {}

  return (
    <Fragment>
      {sidebar ? (
        <Fragment>
          <Box
            sx={{
              userSelect: 'none',
              cursor: 'pointer',

              /** Icon-only rail triggers must shrink-wrap so a footer row can fit bell + other icons */
              width: sidebarTrigger?.iconOnly ? 'auto' : '100%',
              display: 'flex',
              justifyContent: 'center',
              flexShrink: sidebarTrigger?.iconOnly ? 0 : undefined
            }}
          >
            {sidebarTrigger?.iconOnly ? (
              <Tooltip title='Notifications'>
                <IconButton
                  aria-label='Notifications'
                  onClick={handleDropdownOpen}
                  sx={{ ...sidebarTrigger.iconButtonSx, ...bellMotionSx }}
                >
                  <Badge
                    color='error'
                    badgeContent={isSocketConnected ? unreadCount() : OFFLINE_LABEL}
                    sx={{
                      '& .MuiBadge-badge': {
                        boxShadow: theme => `0 0 0 2px ${theme.palette.background.paper}`
                      }
                    }}
                  >
                    <Box
                      component='span'
                      ref={bellAnchorRef}
                      data-notification-bell-anchor='true'
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 0
                      }}
                    >
                      <Icon
                        fontSize={24}
                        icon={sidebarTrigger.bellIcon ?? 'tabler:bell'}
                        color={sidebarTrigger.bellIconColor ?? '#fff'}
                      />
                    </Box>
                  </Badge>
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton onClick={handleDropdownOpen} sx={bellMotionSx}>
                <Stack direction={'row'} alignItems={'center'} className='testiongsdfsd'>
                  <Box
                    component='span'
                    ref={bellAnchorRef}
                    data-notification-bell-anchor='true'
                    sx={{ display: 'inline-flex', lineHeight: 0 }}
                  >
                    <Icon fontSize={24} icon='tabler:bell' color='#fff' />
                  </Box>
                  {!navCollapsed && (
                    <Typography variant='body1' sx={{ color: '#fff', paddingLeft: 2 }}>
                      Notifications
                    </Typography>
                  )}
                  <Badge
                    color='error'
                    badgeContent={isSocketConnected ? unreadCount() : OFFLINE_LABEL}
                    sx={{
                      '& .MuiBadge-badge': {
                        boxShadow: theme => `0 0 0 2px ${theme.palette.background.paper}`
                      }
                    }}
                  ></Badge>
                </Stack>
              </IconButton>
            )}
          </Box>
          <Drawer className='gyg-drawer' open={Boolean(anchorEl)} anchor='right'>
            <Stack
              direction={'column'}
              sx={{
                '& .MuiCard-root': {
                  boxShadow: 'none'
                }
              }}
            >
              <Stack
                height={72}
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                position={'sticky'}
                zIndex={1}
                sx={{ padding: '32px 24px 8px 24px' }}
              >
                <Stack direction={'row'} gap={5} alignItems={'center'} flexWrap='wrap' useFlexGap>
                  <Typography variant='h1'>Notifications</Typography>
                  <Box sx={{ backgroundColor: '#fbefc7', padding: '6px 10px', borderRadius: '4px' }}>
                    <Typography variant='body1' fontWeight={400} fontSize={16}>{`${unreadCount()} new`}</Typography>
                  </Box>
                  {unreadCount() > 0 ? (
                    <Button
                      variant='text'
                      size='small'
                      onClick={handleMarkAllRead}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Mark all as read
                    </Button>
                  ) : null}
                </Stack>
                <Stack direction='row' alignItems='center' spacing={0.5}>
                  <Tooltip title={soundEnabled ? 'Mute notification sounds' : 'Turn notification sounds on'}>
                    <IconButton
                      aria-label={soundEnabled ? 'Mute notification sounds' : 'Enable notification sounds'}
                      onClick={e => {
                        e.stopPropagation()
                        toggleNotificationSound()
                      }}
                      sx={{ color: 'text.secondary' }}
                      size='small'
                    >
                      <Icon fontSize={26} icon={soundEnabled ? 'tabler:volume' : 'tabler:volume-off'} />
                    </IconButton>
                  </Tooltip>
                  <IconButton aria-label='close' onClick={closePanel} sx={{ color: '#000' }}>
                    <Icon icon='ic:baseline-close' fontSize={32} />
                  </IconButton>
                </Stack>
              </Stack>

              <Stack direction='row' alignItems='center' gap={1} sx={{ px: 3, pt: 1, pb: 0.5 }}>
                <TextField
                  size='small'
                  fullWidth
                  placeholder='Search notifications…'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Icon icon='tabler:search' fontSize={20} />
                      </InputAdornment>
                    ),
                    ...(searchQuery
                      ? {
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton size='small' onClick={() => setSearchQuery('')}>
                                <Icon icon='tabler:x' fontSize={18} />
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                      : {})
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
                {notifications.length > 0 && (
                  <Tooltip title='Clear all notifications'>
                    <IconButton
                      aria-label='Clear all notifications'
                      onClick={handleClearAll}
                      size='small'
                      sx={{ flexShrink: 0, color: 'text.secondary' }}
                    >
                      <Icon icon='tabler:trash' fontSize={22} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              <Stack height={'calc(100vh - 300px)'} overflow={'auto'} direction={'column'} padding={'24px 24px 0 24px'}>
                {filteredNotifications.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, opacity: 0.6 }}>
                    <Icon icon={searchQuery ? 'tabler:search-off' : 'tabler:bell-off'} fontSize={48} />
                    <Typography variant='body1' sx={{ mt: 2, color: 'text.secondary' }}>
                      {searchQuery ? 'No matching notifications' : 'No notifications'}
                    </Typography>
                  </Box>
                ) : null}
                {filteredNotifications.map((notification: NotificationsType, index: number) => {
                  const isUnread = notification.read !== true

                  return (
                    <Section
                      sx={{
                        boxShadow: 'unset',
                        borderRadius: '16px',
                        padding: '15px',
                        marginY: 2,
                        ...(isUnread
                          ? {
                              borderLeft: t => `4px solid ${t.palette.primary.main}`,
                              bgcolor: t => alpha(t.palette.primary.main, 0.06)
                            }
                          : {})
                      }}
                      key={notification.clientKey || `notification-${index}`}
                    >
                      <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Stack
                          sx={{
                            mr: 2,
                            ml: 1,
                            flex: '1 1',
                            display: 'flex',
                            overflow: 'hidden',
                            flexDirection: 'column',
                            minWidth: 0
                          }}
                        >
                          <Typography variant='body1' sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {notification.title}
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            {notification.subtitle}
                          </Typography>
                        </Stack>
                        {isUnread ? (
                          <Tooltip title='Mark as read'>
                            <IconButton
                              aria-label='Mark notification as read'
                              size='small'
                              onClick={() => handleMarkOneRead(notification.clientKey)}
                              sx={{ flexShrink: 0, mt: -0.5 }}
                            >
                              <Icon icon='tabler:check' fontSize={22} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <Typography variant='body2' sx={{ flexShrink: 0, alignSelf: 'flex-start', pt: 0.25 }}>
                          {notification.meta}
                        </Typography>
                      </Box>
                    </Section>
                  )
                })}
              </Stack>
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'flex-end'}
                position={'sticky'}
                height={100}
                bottom={0}
                zIndex={1}
                gap={3}
                sx={{
                  padding: '24px 32px 32px 32px',
                  boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)',
                  backgroundColor: 'rgba(255, 255, 255, 1)'
                }}
              >
                <GygButton color='secondary' size='large' text={'Close'} variant='contained' onClick={closePanel} />
              </Stack>
            </Stack>
          </Drawer>
        </Fragment>
      ) : (
        <>
          <IconButton
            color='inherit'
            aria-haspopup='true'
            onClick={handleDropdownOpen}
            aria-controls='customized-menu'
            sx={bellMotionSx}
          >
            <Badge
              color='error'
              badgeContent={isSocketConnected ? unreadCount() : OFFLINE_LABEL}
              sx={{
                '& .MuiBadge-badge': {
                  boxShadow: theme => `0 0 0 2px ${theme.palette.background.paper}`
                }
              }}
            >
              <Box
                component='span'
                ref={bellAnchorRef}
                data-notification-bell-anchor='true'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 0
                }}
              >
                <Icon fontSize={24} icon='tabler:bell' color='#fff' />
              </Box>
            </Badge>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closePanel}
            anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
          >
            <MenuItem
              disableRipple
              disableTouchRipple
              sx={{ cursor: 'default', userSelect: 'auto', backgroundColor: 'transparent !important' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  gap: 2,
                  flexWrap: 'wrap'
                }}
              >
                <Typography variant='h5' sx={{ cursor: 'text' }}>
                  Notifications
                </Typography>
                <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
                  <CustomChip skin='light' size='small' color='primary' label={`${unreadCount()} New`} />
                  {unreadCount() > 0 ? (
                    <Button variant='text' size='small' onClick={handleMarkAllRead} sx={{ textTransform: 'none' }}>
                      Mark all read
                    </Button>
                  ) : null}
                  <Tooltip title={soundEnabled ? 'Mute notification sounds' : 'Turn notification sounds on'}>
                    <IconButton
                      aria-label={soundEnabled ? 'Mute notification sounds' : 'Enable notification sounds'}
                      size='small'
                      onClick={e => {
                        e.stopPropagation()
                        toggleNotificationSound()
                      }}
                    >
                      <Icon fontSize={22} icon={soundEnabled ? 'tabler:volume' : 'tabler:volume-off'} />
                    </IconButton>
                  </Tooltip>
                  {notifications.length > 0 && (
                    <Tooltip title='Clear all notifications'>
                      <IconButton
                        aria-label='Clear all notifications'
                        size='small'
                        onClick={e => {
                          e.stopPropagation()
                          handleClearAll()
                        }}
                      >
                        <Icon fontSize={22} icon='tabler:trash' />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            </MenuItem>
            <MenuItem
              disableRipple
              disableTouchRipple
              sx={{ cursor: 'default', userSelect: 'auto', backgroundColor: 'transparent !important', py: 1 }}
            >
              <TextField
                size='small'
                fullWidth
                placeholder='Search notifications…'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Icon icon='tabler:search' fontSize={18} />
                    </InputAdornment>
                  ),
                  ...(searchQuery
                    ? {
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton size='small' onClick={() => setSearchQuery('')}>
                              <Icon icon='tabler:x' fontSize={16} />
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    : {})
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </MenuItem>
            <ScrollWrapper hidden={hidden}>
              {filteredNotifications.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, opacity: 0.6 }}>
                  <Icon icon={searchQuery ? 'tabler:search-off' : 'tabler:bell-off'} fontSize={40} />
                  <Typography variant='body2' sx={{ mt: 1.5, color: 'text.secondary' }}>
                    {searchQuery ? 'No matching notifications' : 'No notifications'}
                  </Typography>
                </Box>
              ) : null}
              {filteredNotifications.map((notification: NotificationsType, index: number) => (
                <MenuItem
                  key={notification.clientKey || `notification-${index}`}
                  disableRipple
                  disableTouchRipple
                  onClick={() => {
                    if (notification.read !== true && notification.clientKey) {
                      handleMarkOneRead(notification.clientKey)
                    }
                  }}
                  sx={{
                    alignItems: 'flex-start',
                    py: 2,
                    ...(notification.read !== true
                      ? {
                          borderLeft: t => `3px solid ${t.palette.primary.main}`,
                          bgcolor: t => alpha(t.palette.primary.main, 0.05)
                        }
                      : {})
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box
                      sx={{
                        mr: 2,
                        ml: 0.5,
                        flex: '1 1',
                        display: 'flex',
                        overflow: 'hidden',
                        flexDirection: 'column',
                        minWidth: 0
                      }}
                    >
                      <MenuItemTitle sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {notification.title}
                      </MenuItemTitle>
                      <MenuItemSubtitle variant='body2'>{notification.subtitle}</MenuItemSubtitle>
                    </Box>
                    <Typography variant='body2' sx={{ color: 'text.disabled', flexShrink: 0 }}>
                      {notification.meta}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </ScrollWrapper>
            {/*  */}
          </Menu>
        </>
      )}
      <Popper
        open={Boolean(highlightBubble && popperAnchor)}
        anchorEl={popperAnchor}
        placement='top'
        transition
        modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
        sx={{ zIndex: theme => theme.zIndex.modal + 4 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              elevation={6}
              sx={{
                maxWidth: 320,
                p: 1.75,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                position: 'relative',
                boxShadow: theme => theme.shadows[8],
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -6,
                  left: '50%',
                  marginLeft: -6,
                  width: 12,
                  height: 12,
                  bgcolor: 'background.paper',
                  borderRight: '1px solid',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  transform: 'rotate(45deg)'
                }
              }}
            >
              <Typography variant='body2' sx={{ lineHeight: 1.45 }}>
                {highlightBubble?.text}
              </Typography>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Fragment>
  )
}

export default NotificationDropdown
